import React, { ReactNode } from 'react'

import { CSS } from '@dnd-kit/utilities'

import { useSortable } from '@dnd-kit/sortable'
import { useTheme } from '@mui/material'

import TableCell from 'components_new/atoms/Table/TableCell'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import NameCell, { NameCellContext } from '../NameCell'
import { CellsComponentType, FlattenedNode, TreeColumn } from '../types'

interface Props {
  anyRowHasChildren: boolean
  CellsComponent?: CellsComponentType
  columns: TreeColumn[]
  disableDrag?: boolean
  editMode?: boolean
  hasChildren: boolean
  node: FlattenedNode
  onToggleExpand: (id: string) => void
  renderName?: (node: FlattenedNode) => ReactNode
}

const TreeDndTableRow = React.forwardRef<HTMLTableRowElement, Props>(
  (props, ref) => {
    const {
      anyRowHasChildren,
      CellsComponent,
      columns,
      disableDrag,
      editMode,
      hasChildren,
      node,
      onToggleExpand,
      renderName
    } = props

    const {
      attributes,
      isDragging,
      listeners,
      setNodeRef,
      transform,
      transition
    } = useSortable({ disabled: !editMode || disableDrag, id: node.id })

    const theme = useTheme()

    const style = {
      backgroundColor: isDragging ? theme.palette.brand.background : undefined,
      transform: CSS.Transform.toString(transform),
      transition
    }

    const combinedRef = (el: HTMLTableRowElement) => {
      setNodeRef(el)

      if (typeof ref === 'function') {
        ref(el)
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTableRowElement | null>).current =
          el
      }
    }

    const contextValue = {
      anyRowHasChildren,
      disableDrag,
      editMode,
      hasChildren,
      listeners,
      node,
      onToggleExpand
    }

    return (
      <NameCellContext.Provider value={contextValue}>
        <TableRow
          hover
          onClick={() => onToggleExpand(node.id)}
          ref={combinedRef}
          sx={{
            cursor: hasChildren ? 'pointer' : undefined,
            ...style
          }}
          {...attributes}
        >
          {CellsComponent ? (
            <CellsComponent node={node} />
          ) : (
            <>
              <NameCell sx={{ width: columns[0]?.width }}>
                {renderName ? (
                  renderName(node)
                ) : (
                  <Text variant="body2">{node.data.name}</Text>
                )}
              </NameCell>
              {columns.slice(1).map((column) => (
                <TableCell key={column.key} sx={{ width: column.width }}>
                  {column.renderCell?.(node)}
                </TableCell>
              ))}
            </>
          )}
        </TableRow>
      </NameCellContext.Provider>
    )
  }
)

TreeDndTableRow.displayName = 'TreeDndTableRow'
export default TreeDndTableRow
